package kpt.core.base.database.infra.dao

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.base.database.infra.entity.ConflictEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class ConflictDao_Impl(
  __db: RoomDatabase,
) : ConflictDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfConflictEntity: EntityInsertAdapter<ConflictEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfConflictEntity = object : EntityInsertAdapter<ConflictEntity>() {
      protected override fun createQuery(): String = "INSERT OR ABORT INTO `framework_write_conflicts` (`id`,`entity`,`entityKey`,`localPayloadJson`,`serverPayloadJson`,`formRoute`,`recordedAtMs`,`resolved`) VALUES (nullif(?, 0),?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: ConflictEntity) {
        statement.bindLong(1, entity.id)
        statement.bindText(2, entity.entity)
        statement.bindText(3, entity.entityKey)
        statement.bindText(4, entity.localPayloadJson)
        statement.bindText(5, entity.serverPayloadJson)
        val _tmpFormRoute: String? = entity.formRoute
        if (_tmpFormRoute == null) {
          statement.bindNull(6)
        } else {
          statement.bindText(6, _tmpFormRoute)
        }
        statement.bindLong(7, entity.recordedAtMs)
        statement.bindLong(8, entity.resolved.toLong())
      }
    }
  }

  public override suspend fun insert(entity: ConflictEntity): Long = performSuspending(__db, false, true) { _connection ->
    val _result: Long = __insertAdapterOfConflictEntity.insertAndReturnId(_connection, entity)
    _result
  }

  public override fun observePending(): Flow<List<ConflictEntity>> {
    val _sql: String = "SELECT * FROM framework_write_conflicts WHERE resolved = 0 ORDER BY recordedAtMs DESC"
    return createFlow(__db, false, arrayOf("framework_write_conflicts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfEntity: Int = getColumnIndexOrThrow(_stmt, "entity")
        val _columnIndexOfEntityKey: Int = getColumnIndexOrThrow(_stmt, "entityKey")
        val _columnIndexOfLocalPayloadJson: Int = getColumnIndexOrThrow(_stmt, "localPayloadJson")
        val _columnIndexOfServerPayloadJson: Int = getColumnIndexOrThrow(_stmt, "serverPayloadJson")
        val _columnIndexOfFormRoute: Int = getColumnIndexOrThrow(_stmt, "formRoute")
        val _columnIndexOfRecordedAtMs: Int = getColumnIndexOrThrow(_stmt, "recordedAtMs")
        val _columnIndexOfResolved: Int = getColumnIndexOrThrow(_stmt, "resolved")
        val _result: MutableList<ConflictEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: ConflictEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpEntity: String
          _tmpEntity = _stmt.getText(_columnIndexOfEntity)
          val _tmpEntityKey: String
          _tmpEntityKey = _stmt.getText(_columnIndexOfEntityKey)
          val _tmpLocalPayloadJson: String
          _tmpLocalPayloadJson = _stmt.getText(_columnIndexOfLocalPayloadJson)
          val _tmpServerPayloadJson: String
          _tmpServerPayloadJson = _stmt.getText(_columnIndexOfServerPayloadJson)
          val _tmpFormRoute: String?
          if (_stmt.isNull(_columnIndexOfFormRoute)) {
            _tmpFormRoute = null
          } else {
            _tmpFormRoute = _stmt.getText(_columnIndexOfFormRoute)
          }
          val _tmpRecordedAtMs: Long
          _tmpRecordedAtMs = _stmt.getLong(_columnIndexOfRecordedAtMs)
          val _tmpResolved: Int
          _tmpResolved = _stmt.getLong(_columnIndexOfResolved).toInt()
          _item = ConflictEntity(_tmpId,_tmpEntity,_tmpEntityKey,_tmpLocalPayloadJson,_tmpServerPayloadJson,_tmpFormRoute,_tmpRecordedAtMs,_tmpResolved)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: Long): ConflictEntity? {
    val _sql: String = "SELECT * FROM framework_write_conflicts WHERE id = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfEntity: Int = getColumnIndexOrThrow(_stmt, "entity")
        val _columnIndexOfEntityKey: Int = getColumnIndexOrThrow(_stmt, "entityKey")
        val _columnIndexOfLocalPayloadJson: Int = getColumnIndexOrThrow(_stmt, "localPayloadJson")
        val _columnIndexOfServerPayloadJson: Int = getColumnIndexOrThrow(_stmt, "serverPayloadJson")
        val _columnIndexOfFormRoute: Int = getColumnIndexOrThrow(_stmt, "formRoute")
        val _columnIndexOfRecordedAtMs: Int = getColumnIndexOrThrow(_stmt, "recordedAtMs")
        val _columnIndexOfResolved: Int = getColumnIndexOrThrow(_stmt, "resolved")
        val _result: ConflictEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpEntity: String
          _tmpEntity = _stmt.getText(_columnIndexOfEntity)
          val _tmpEntityKey: String
          _tmpEntityKey = _stmt.getText(_columnIndexOfEntityKey)
          val _tmpLocalPayloadJson: String
          _tmpLocalPayloadJson = _stmt.getText(_columnIndexOfLocalPayloadJson)
          val _tmpServerPayloadJson: String
          _tmpServerPayloadJson = _stmt.getText(_columnIndexOfServerPayloadJson)
          val _tmpFormRoute: String?
          if (_stmt.isNull(_columnIndexOfFormRoute)) {
            _tmpFormRoute = null
          } else {
            _tmpFormRoute = _stmt.getText(_columnIndexOfFormRoute)
          }
          val _tmpRecordedAtMs: Long
          _tmpRecordedAtMs = _stmt.getLong(_columnIndexOfRecordedAtMs)
          val _tmpResolved: Int
          _tmpResolved = _stmt.getLong(_columnIndexOfResolved).toInt()
          _result = ConflictEntity(_tmpId,_tmpEntity,_tmpEntityKey,_tmpLocalPayloadJson,_tmpServerPayloadJson,_tmpFormRoute,_tmpRecordedAtMs,_tmpResolved)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun markResolved(id: Long) {
    val _sql: String = "UPDATE framework_write_conflicts SET resolved = 1 WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
